// src/admin/components/buildings/BuildingView.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  RiBuilding3Line,
  RiArrowLeftLine,
  RiArrowDownSLine,
  RiCheckLine,
  RiAlertLine,
  RiRefreshLine,
  RiMapPinLine,
  RiEdit2Line,
  RiRulerLine,
  RiInformationLine,
  RiAddLine,
  RiMoneyDollarCircleLine,
  RiCalendarLine,
  RiFileChartLine,
  RiSearchLine,
  RiArrowRightSLine,
  RiLightbulbLine,
  RiExpandUpDownLine,
  RiCollapseVerticalLine,
  RiGridLine,
  RiListUnordered,
  RiBox3Line,
  RiLayoutMasonryLine,
  RiBarChartBoxLine,
  RiSortAsc,
  RiSortDesc,
  RiUserLine,
} from 'react-icons/ri';
import { getAllApartments } from '../../../models/apartmentModel';
import { isLeaseExpired, getTenantLeaseStatus } from '../../../models/tenantModel';
import { useTheme } from '../../../contexts/ThemeContext';

/* ─── Helpers ────────────────────────────────────────────────────────── */
const unitStatusHex = (apt) => {
  const hasTenant = apt.tenants?.length > 0;
  const rs = apt.tenants?.[0]?.rentStatus || apt.rentStatus;
  if (apt.status === 'maintenance') return '#f59e0b';
  if (!hasTenant && apt.status === 'vacant') return '#3b82f6';
  if (rs === 'overdue') return '#ef4444';
  if (rs === 'partial') return '#f59e0b';
  if (rs === 'pending') return '#71717a';
  return '#10b981';
};

const TYPE_COLORS = {
  'Small':        'bg-slate-500/20 text-slate-300 border-slate-500/40',
  'Medium':       'bg-blue-500/20 text-blue-300 border-blue-500/40',
  'Large':        'bg-violet-500/20 text-violet-300 border-violet-500/40',
  'Extra Large':  'bg-purple-500/20 text-purple-300 border-purple-500/40',
  'Cold Storage': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
  'Drive-In':     'bg-orange-500/20 text-orange-300 border-orange-500/40',
};
const typeChipClass = (type) => TYPE_COLORS[type] || 'bg-zinc-500/20 text-zinc-300 border-zinc-500/40';

/* ─── Component ─────────────────────────────────────────────────────── */
const BuildingView = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  const [building, setBuilding]     = useState(null);
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [blockPlans, setBlockPlans] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType]     = useState('all');
  const [sortOrder, setSortOrder]   = useState(() => localStorage.getItem('complexView_sort') || 'asc');
  const [viewMode, setViewMode]     = useState(() => localStorage.getItem('complexView_mode') || 'list');
  const [expandedBlocks, setExpandedBlocks] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  /* ── Fetch ──────────────────────────────────────────────────────────── */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true); setError(null);
        const response = await getAllApartments({ buildingId: id });
        if (response?.apartments?.length > 0) {
          const apts = response.apartments;
          setBuilding({
            id,
            name: apts[0].buildingName,
            address: apts[0].buildingAddress,
            totalUnits: apts.length,
          });
          setApartments(apts);

          // Group by floor → "Block"
          const blocks = {};
          apts.forEach(apt => {
            const key = apt.floor || 1;
            if (!blocks[key]) blocks[key] = [];
            blocks[key].push(apt);
          });
          Object.keys(blocks).forEach(k => {
            blocks[k].sort((a, b) => {
              const na = parseInt(a.apartmentNumber.replace(/\D/g,''));
              const nb = parseInt(b.apartmentNumber.replace(/\D/g,''));
              return isNaN(na)||isNaN(nb) ? a.apartmentNumber.localeCompare(b.apartmentNumber) : na-nb;
            });
          });
          setBlockPlans(blocks);
          const firstKey = Object.keys(blocks).sort((a,b)=>Number(a)-Number(b))[0];
          if (firstKey) setExpandedBlocks(new Set([firstKey]));
        } else {
          setError('no_units_in_complex');
        }
      } catch { setError('failed_to_load'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [id, refreshTrigger]);

  useEffect(() => {
    const h = () => setRefreshTrigger(p=>p+1);
    window.addEventListener('focus', h);
    return () => window.removeEventListener('focus', h);
  }, []);

  useEffect(() => { localStorage.setItem('complexView_sort', sortOrder); }, [sortOrder]);
  useEffect(() => { localStorage.setItem('complexView_mode', viewMode); }, [viewMode]);

  /* ── Toggles ─────────────────────────────────────────────────────── */
  const toggleBlock = (key) => {
    setExpandedBlocks(prev => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  };
  const toggleAll = () => {
    if (allExpanded) { setExpandedBlocks(new Set()); setAllExpanded(false); }
    else { setExpandedBlocks(new Set(Object.keys(blockPlans))); setAllExpanded(true); }
  };

  /* ── Stats ──────────────────────────────────────────────────────── */
  const stats = useMemo(() => {
    if (!apartments.length) return { total:0, vacant:0, occupied:0, maintenance:0, overdue:0, totalM2:'0', vacantM2:'0', utilization:'0.0' };
    const vacant      = apartments.filter(a => !a.tenants?.length && a.status==='vacant').length;
    const occupied    = apartments.filter(a => a.tenants?.length || a.status==='occupied').length;
    const maintenance = apartments.filter(a => !a.tenants?.length && a.status==='maintenance').length;
    const overdue     = apartments.filter(a => (a.tenants?.[0]?.rentStatus||a.rentStatus)==='overdue').length;
    const totalM2     = apartments.reduce((s,a) => s + (parseFloat(a.size)||0), 0);
    const vacantM2    = apartments.filter(a=>!a.tenants?.length&&a.status==='vacant').reduce((s,a)=>s+(parseFloat(a.size)||0),0);
    const utilization = apartments.length ? ((occupied/apartments.length)*100).toFixed(1) : '0.0';
    return { total:apartments.length, vacant, occupied, maintenance, overdue, totalM2:totalM2.toFixed(0), vacantM2:vacantM2.toFixed(0), utilization };
  }, [apartments]);

  /* ── Sorted blocks & filter ─────────────────────────────────────── */
  const sortedBlocks = useMemo(() => {
    const nums = Object.keys(blockPlans).map(Number);
    nums.sort((a,b) => sortOrder==='asc' ? a-b : b-a);
    return nums;
  }, [blockPlans, sortOrder]);

  const filterApt = (apt) => {
    const hasTenant  = apt.tenants?.length > 0;
    const pT         = hasTenant ? apt.tenants[0] : null;
    const rs         = pT?.rentStatus || apt.rentStatus;
    const q          = searchTerm.toLowerCase();
    const matchSearch = !q ||
      apt.apartmentNumber.toLowerCase().includes(q) ||
      (apt.ownerName?.toLowerCase().includes(q)) ||
      (pT?.name?.toLowerCase().includes(q)) ||
      (apt.tenantName?.toLowerCase().includes(q));
    const catStatus  = hasTenant ? 'occupied' : apt.status;
    const matchStatus =
      filterStatus==='all' ||
      (filterStatus==='occupied' && (hasTenant||apt.status==='occupied')) ||
      (filterStatus==='overdue' && rs==='overdue') ||
      catStatus===filterStatus;
    const matchType  = filterType==='all' || (apt.type||'').toLowerCase()===filterType.toLowerCase();
    return matchSearch && matchStatus && matchType;
  };

  /* ── Loading / Error ─────────────────────────────────────────────── */
  if (loading) return (
    <div dir={isRTL?'rtl':'ltr'} className={`rounded-xl border shadow-lg p-16 text-center ${isDarkMode?'bg-zinc-900 border-zinc-800':'bg-white border-zinc-200'}`}>
      <div className="flex flex-col items-center gap-4">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-red-500 border-t-transparent"/>
        <p className={`text-lg font-medium ${isDarkMode?'text-zinc-300':'text-zinc-600'}`}>Loading complex…</p>
      </div>
    </div>
  );

  if (error || !building) return (
    <div dir={isRTL?'rtl':'ltr'} className={`rounded-xl border shadow-lg p-8 ${isDarkMode?'bg-zinc-900 border-zinc-800':'bg-white border-zinc-200'}`}>
      <div className={`p-6 rounded-lg flex flex-col items-center text-center ${isDarkMode?'bg-red-900/20 text-red-400 border border-red-900/50':'bg-red-50 text-red-700 border border-red-200'}`}>
        <RiAlertLine className="text-4xl mb-3"/>
        <p className="font-semibold mb-6">{t('no_showrooms_in_complex', 'No showroom units found in this complex yet.')}</p>
        <div className="flex gap-3">
          {id && (
            <Link to={`/admin/apartments/add?buildingId=${id}`}
              className="px-5 py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-2 font-semibold">
              <RiAddLine/> {t('add_first_showroom', 'Add First Showroom')}
            </Link>
          )}
          <button onClick={()=>navigate('/admin/buildings')}
            className={`px-5 py-2.5 rounded-lg flex items-center gap-2 font-semibold ${isDarkMode?'bg-zinc-800 text-zinc-300 hover:bg-zinc-700':'bg-zinc-200 text-zinc-800 hover:bg-zinc-300'}`}>
            <RiArrowLeftLine className="rtl:rotate-180"/> Back
          </button>
        </div>
      </div>
    </div>
  );

  /* ════════════════════════════════════════════════════════════════════ */
  return (
    <div dir={isRTL?'rtl':'ltr'} className="space-y-5">

      {/* ── HEADER ────────────────────────────────────────────────────── */}
      <div className={`rounded-xl border shadow-md overflow-hidden ${isDarkMode?'bg-zinc-900 border-zinc-800':'bg-white border-zinc-200'}`}>
        <div className="h-1.5 bg-gradient-to-r from-red-600 via-orange-500 to-red-600"/>
        <div className="p-5 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={()=>navigate('/admin/buildings')}
              className={`p-2.5 rounded-full shrink-0 ${isDarkMode?'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700':'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}>
              <RiArrowLeftLine size={20} className="rtl:rotate-180"/>
            </button>
            <div className={`p-2.5 rounded-lg shrink-0 ${isDarkMode?'bg-red-900/30 text-red-400':'bg-red-50 text-red-600'}`}>
              <RiBuilding3Line size={24}/>
            </div>
            <div>
              <h2 className={`text-2xl font-bold ${isDarkMode?'text-white':'text-zinc-900'}`}>{building.name}</h2>
              {building.address && (
                <p className={`text-sm flex items-center gap-1.5 mt-0.5 ${isDarkMode?'text-zinc-400':'text-zinc-500'}`}>
                  <RiMapPinLine size={14}/>{building.address}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/admin/apartments/add?buildingId=${building.id}`}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2 shadow-sm">
              <RiAddLine/> {t('add_showroom_btn', 'Add Showroom')}
            </Link>
            <Link to={`/admin/buildings/${building.id}/edit`}
              className={`px-4 py-2 text-sm font-semibold rounded-lg border flex items-center gap-2 ${isDarkMode?'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700':'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'}`}>
              <RiEdit2Line/> {t('edit_complex', 'Edit Complex')}
            </Link>
            <button onClick={()=>navigate(`/admin/reports?buildingId=${building.id}`)}
              className={`px-4 py-2 text-sm font-semibold rounded-lg border flex items-center gap-2 ${isDarkMode?'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700':'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'}`}>
              <RiFileChartLine/> {t('reports', 'Reports')}
            </button>
          </div>
        </div>
      </div>

      {/* ── KPI STATS ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label:t('total_showrooms_label', 'Total Showrooms'),    value:stats.total,       sub:`${Number(stats.totalM2).toLocaleString()} ${t('m2_total_area', 'm² total area')}`,         icon:<RiBox3Line size={22}/>,         colorCls:isDarkMode?'bg-zinc-800 border-zinc-700':'bg-zinc-50 border-zinc-200',              iconCls:isDarkMode?'bg-zinc-700 text-zinc-300':'bg-zinc-100 text-zinc-600' },
          { label:t('available', 'Available'),           value:stats.vacant,      sub:`${Number(stats.vacantM2).toLocaleString()} ${t('m2_free', 'm² free')}`,              icon:<RiCheckLine size={22}/>,        colorCls:isDarkMode?'bg-blue-900/20 border-blue-900/40':'bg-blue-50 border-blue-100',       iconCls:isDarkMode?'bg-blue-900/40 text-blue-400':'bg-blue-100 text-blue-600' },
          { label:t('rented_sold', 'Rented / Sold'),       value:stats.occupied,    sub:`${stats.utilization}% ${t('utilization', 'utilization')}`,                               icon:<RiBarChartBoxLine size={22}/>,  colorCls:isDarkMode?'bg-emerald-900/20 border-emerald-900/40':'bg-emerald-50 border-emerald-100', iconCls:isDarkMode?'bg-emerald-900/40 text-emerald-400':'bg-emerald-100 text-emerald-600' },
          { label:t('attention_required', 'Attention Required'),  value:stats.overdue, sub:stats.maintenance>0?`${stats.maintenance} ${t('in_maintenance', 'in maintenance')}`:t('all_payments_current', 'All payments current'), icon:<RiAlertLine size={22}/>, colorCls:isDarkMode?'bg-amber-900/20 border-amber-900/40':'bg-amber-50 border-amber-100', iconCls:isDarkMode?'bg-amber-900/40 text-amber-400':'bg-amber-100 text-amber-600' },
        ].map((s,i) => (
          <div key={i} className={`p-4 rounded-xl border ${s.colorCls} flex items-center gap-3 hover:shadow-md transition-shadow`}>
            <div className={`p-2.5 rounded-lg shrink-0 ${s.iconCls}`}>{s.icon}</div>
            <div>
              <p className={`text-[10px] font-bold uppercase tracking-widest ${isDarkMode?'text-zinc-500':'text-zinc-400'}`}>{s.label}</p>
              <p className={`text-3xl font-black tracking-tight ${isDarkMode?'text-white':'text-zinc-900'}`}>{s.value}</p>
              <p className={`text-xs mt-0.5 ${isDarkMode?'text-zinc-500':'text-zinc-400'}`}>{s.sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── CONTROLS ──────────────────────────────────────────────────── */}
      <div className={`rounded-xl border shadow-sm p-4 ${isDarkMode?'bg-zinc-900 border-zinc-800':'bg-white border-zinc-200'}`}>
        <div className="flex flex-col xl:flex-row gap-3 items-start xl:items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 w-full xl:max-w-sm">
            <RiSearchLine className={`absolute top-1/2 -translate-y-1/2 start-3 ${isDarkMode?'text-zinc-500':'text-zinc-400'}`} size={16}/>
            <input value={searchTerm} onChange={e=>setSearchTerm(e.target.value)}
              placeholder="Search by unit #, client, owner…"
              className={`w-full ps-9 pe-4 py-2.5 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-red-500/40 ${isDarkMode?'bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500':'bg-zinc-50 border-zinc-200 text-zinc-900 placeholder:text-zinc-400'}`}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Status filter */}
            <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
              className={`text-sm px-3 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-red-500/40 ${isDarkMode?'bg-zinc-800 border-zinc-700 text-zinc-200':'bg-zinc-50 border-zinc-200 text-zinc-700'}`}>
              <option value="all">{t('all_statuses', 'All Statuses')}</option>
              <option value="vacant">{t('vacant_available', 'Vacant / Available')}</option>
              <option value="occupied">{t('rented_sold', 'Rented / Sold')}</option>
              <option value="overdue">{t('overdue_payment', 'Overdue Payment')}</option>
              <option value="maintenance">{t('maintenance', 'Maintenance')}</option>
            </select>

            {/* Type filter */}
            <select value={filterType} onChange={e=>setFilterType(e.target.value)}
              className={`text-sm px-3 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-red-500/40 ${isDarkMode?'bg-zinc-800 border-zinc-700 text-zinc-200':'bg-zinc-50 border-zinc-200 text-zinc-700'}`}>
              <option value="all">All Types</option>
              <option value="Small">Small</option>
              <option value="Medium">Medium</option>
              <option value="Large">Large</option>
              <option value="Extra Large">Extra Large</option>
              <option value="Cold Storage">Cold Storage</option>
              <option value="Drive-In">Drive-In</option>
            </select>

            {/* Sort */}
            <button onClick={()=>setSortOrder(p=>p==='asc'?'desc':'asc')}
              className={`flex items-center gap-1.5 text-sm px-3 py-2.5 rounded-lg border ${isDarkMode?'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700':'bg-zinc-50 border-zinc-200 text-zinc-700 hover:bg-zinc-100'}`}>
              {sortOrder==='asc' ? <RiSortAsc size={15}/> : <RiSortDesc size={15}/>}
              {sortOrder==='asc' ? 'Zone 1 First' : 'Last Zone First'}
            </button>

            {/* Expand/Collapse */}
            <button onClick={toggleAll}
              className={`flex items-center gap-1.5 text-sm px-3 py-2.5 rounded-lg border ${isDarkMode?'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700':'bg-zinc-50 border-zinc-200 text-zinc-700 hover:bg-zinc-100'}`}>
              {allExpanded ? <><RiCollapseVerticalLine size={15}/> Collapse All</> : <><RiExpandUpDownLine size={15}/> Expand All</>}
            </button>

            {/* View toggle */}
            <div className={`flex rounded-lg overflow-hidden border ${isDarkMode?'border-zinc-700':'border-zinc-200'}`}>
              <button onClick={()=>setViewMode('list')}
                className={`flex items-center justify-center w-10 h-10 ${viewMode==='list'?'bg-red-600 text-white':isDarkMode?'bg-zinc-800 text-zinc-400 hover:bg-zinc-700':'bg-zinc-50 text-zinc-500 hover:bg-zinc-100'}`}
                title="List View"><RiListUnordered size={17}/></button>
              <button onClick={()=>setViewMode('grid')}
                className={`flex items-center justify-center w-10 h-10 ${viewMode==='grid'?'bg-red-600 text-white':isDarkMode?'bg-zinc-800 text-zinc-400 hover:bg-zinc-700':'bg-zinc-50 text-zinc-500 hover:bg-zinc-100'}`}
                title="Map View"><RiGridLine size={17}/></button>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className={`mt-4 pt-4 border-t flex flex-wrap gap-x-5 gap-y-2 ${isDarkMode?'border-zinc-800':'border-zinc-100'}`}>
          {[
            {c:'#3b82f6', l:'Vacant'},
            {c:'#10b981', l:'Rented / Sold'},
            {c:'#ef4444', l:'Overdue'},
            {c:'#f59e0b', l:'Partial / Maint.'},
            {c:'#71717a', l:'Pending'},
          ].map(x=>(
            <div key={x.l} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{backgroundColor:x.c, boxShadow:`0 0 6px ${x.c}70`}}/>
              <span className={`text-xs font-medium ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{x.l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── FLOOR PLAN / MAP VIEW ─────────────────────────────────────── */}
      {viewMode==='grid' && (
        <div className={`rounded-xl border shadow-md overflow-hidden ${isDarkMode?'bg-zinc-900 border-zinc-800':'bg-white border-zinc-200'}`}>
          <div className={`px-5 py-3.5 border-b flex items-center gap-2 ${isDarkMode?'border-zinc-800 bg-zinc-800/40':'border-zinc-100 bg-zinc-50'}`}>
            <RiGridLine className={isDarkMode?'text-red-400':'text-red-600'} size={18}/>
            <span className={`text-sm font-bold ${isDarkMode?'text-white':'text-zinc-800'}`}>Floor Plan View</span>
            <span className={`text-xs ms-1 ${isDarkMode?'text-zinc-500':'text-zinc-400'}`}>— matches architect layout</span>
          </div>
          <div className="p-5 overflow-x-auto space-y-8">
            {sortedBlocks.map(blockNum => {
              const all = blockPlans[blockNum];
              if (!all?.length) return null;
              const visible = all.filter(filterApt);
              if (!visible.length && (searchTerm||filterStatus!=='all'||filterType!=='all')) return null;

              // Sort by unit number
              const sorted = [...all].sort((a,b) => parseInt(a.apartmentNumber) - parseInt(b.apartmentNumber));
              const nums = sorted.map(a => parseInt(a.apartmentNumber));
              const min = nums[0], max = nums[nums.length-1];

              // ── Use row_position from DB (front / back / corner) ──────────────
              const hasPosData = sorted.some(a => a.rowPosition);
              let bottomRow, topRow, cornerUnits;
              if (hasPosData) {
                bottomRow   = sorted.filter(a => a.rowPosition === 'front');
                topRow      = [...sorted.filter(a => a.rowPosition === 'back')].reverse(); // high→low
                cornerUnits = sorted.filter(a => a.rowPosition === 'corner');
              } else {
                // fallback: even split
                const half = Math.floor(sorted.length / 2);
                bottomRow   = sorted.slice(0, half);
                topRow      = [...sorted.slice(half)].reverse();
                cornerUnits = [];
              }

              const UnitCell = ({ apt, small = false }) => {
                const color = unitStatusHex(apt);
                const isVis = filterApt(apt);
                const hasTenant = apt.tenants?.length > 0;
                const pT = hasTenant ? apt.tenants[0] : null;
                const w = small ? 'w-11 h-11' : 'w-16 h-16';
                return (
                  <div className="relative group shrink-0">
                    <Link to={`/admin/apartments/${apt.id}`}
                      className={`flex flex-col items-center justify-center ${w} rounded-lg border-2 transition-all hover:-translate-y-1 hover:shadow-lg ${!isVis?'opacity-20 grayscale':''} ${isDarkMode?'bg-zinc-800':'bg-white'}`}
                      style={{borderColor:color, boxShadow:isVis?`0 2px 10px ${color}30`:'none'}}>
                      <span className={`${small?'text-[10px]':'text-sm'} font-black font-mono ${isDarkMode?'text-zinc-100':'text-zinc-900'}`}>{apt.apartmentNumber}</span>
                      <div className={`${small?'w-3':'w-5'} h-0.5 mt-1 rounded-full`} style={{backgroundColor:color}}/>
                      {apt.size && <span className={`${small?'text-[7px]':'text-[8px]'} mt-0.5 ${isDarkMode?'text-zinc-500':'text-zinc-400'}`}>{apt.size}m²</span>}
                    </Link>
                    {/* Tooltip */}
                    <div className={`pointer-events-none absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-50 w-44 p-2.5 rounded-xl shadow-2xl text-xs opacity-0 group-hover:opacity-100 transition-opacity border ${isDarkMode?'bg-zinc-800 border-zinc-700 text-zinc-200':'bg-white border-zinc-200 text-zinc-800'}`}>
                      <div className="flex justify-between items-center mb-1.5 pb-1.5 border-b border-zinc-500/20">
                        <span className="font-bold">Unit {apt.apartmentNumber}</span>
                        <span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:color}}/>
                      </div>
                      {apt.type && <p className="flex justify-between"><span className={isDarkMode?'text-zinc-500':'text-zinc-400'}>Type</span><span className="font-semibold">{apt.type}</span></p>}
                      {apt.size && <p className="flex justify-between"><span className={isDarkMode?'text-zinc-500':'text-zinc-400'}>Size</span><span className="font-semibold">{apt.size} m²</span></p>}
                      {hasTenant && <p className="text-emerald-400 font-bold mt-1.5 truncate">{pT?.name||apt.tenantName}</p>}
                      {!hasTenant && apt.ownerName && <p className="text-blue-400 font-bold mt-1.5 truncate">{apt.ownerName}</p>}
                    </div>
                  </div>
                );
              };

              return (
                <div key={blockNum}>
                  {/* Zone header */}
                  <div className={`flex items-center gap-3 mb-3`}>
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-bold text-sm ${isDarkMode?'bg-zinc-800 border border-zinc-700 text-zinc-300':'bg-zinc-100 border border-zinc-200 text-zinc-700'}`}>
                      <RiLayoutMasonryLine className={isDarkMode?'text-red-400':'text-red-500'}/>
                      {t('zone', 'ZONE')} {blockNum}
                    </div>
                    <span className={`text-xs ${isDarkMode?'text-zinc-500':'text-zinc-400'}`}>{all.length} {t('units', 'units')} · {bottomRow.length} {t('front', 'front')} · {topRow.length} {t('back', 'back')}{cornerUnits.length?` · ${cornerUnits.length} ${t('corner', 'corner')}`:''}</span>
                  </div>

                  {/* Floor plan grid */}
                  <div className={`inline-flex gap-2 p-4 rounded-2xl border ${isDarkMode?'bg-zinc-900/60 border-zinc-700/60':'bg-zinc-50 border-zinc-200'}`}>
                    {/* Main block: top + bottom rows stacked */}
                    <div className="flex flex-col gap-2">
                      {/* TOP ROW (back row — reversed in plan) */}
                      <div className={`flex gap-1.5 px-2 py-1 rounded-xl border border-dashed ${isDarkMode?'border-zinc-700 bg-zinc-800/30':'border-zinc-200 bg-zinc-100/50'}`}>
                        {topRow.map(apt => <UnitCell key={apt.id} apt={apt}/>)}
                      </div>
                      {/* Divider representing the building wall */}
                      <div className={`h-2 rounded-full mx-2 ${isDarkMode?'bg-zinc-700':'bg-zinc-300'}`}/>
                      {/* BOTTOM ROW (front row) */}
                      <div className={`flex gap-1.5 px-2 py-1 rounded-xl border border-dashed ${isDarkMode?'border-zinc-700 bg-zinc-800/30':'border-zinc-200 bg-zinc-100/50'}`}>
                        {bottomRow.map(apt => <UnitCell key={apt.id} apt={apt}/>)}
                      </div>
                    </div>

                    {/* CORNER COLUMN (corner units stacked vertically on the right) */}
                    {cornerUnits.length > 0 && (
                      <div className={`flex flex-col gap-1.5 justify-center ms-2 ps-2 border-s ${isDarkMode?'border-zinc-700':'border-zinc-300'}`}>
                        {[...cornerUnits].reverse().map(apt => <UnitCell key={apt.id} apt={apt} small/>)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── LIST / ACCORDION VIEW ─────────────────────────────────────── */}
      {viewMode==='list' && (
        <div className="space-y-3">
          {sortedBlocks.length===0 ? (
            <div className={`rounded-xl border p-12 text-center ${isDarkMode?'bg-zinc-900 border-zinc-800':'bg-white border-zinc-200'}`}>
              <RiInformationLine className={`mx-auto text-5xl mb-4 ${isDarkMode?'text-zinc-700':'text-zinc-300'}`}/>
              <h3 className={`text-lg font-bold ${isDarkMode?'text-zinc-300':'text-zinc-700'}`}>{t('no_showrooms_found_title', 'No Showrooms Found')}</h3>
              <p className={`text-sm mt-2 mb-6 ${isDarkMode?'text-zinc-500':'text-zinc-400'}`}>Try adjusting search or filters.</p>
              {(searchTerm||filterStatus!=='all'||filterType!=='all') && (
                <button onClick={()=>{setSearchTerm('');setFilterStatus('all');setFilterType('all');}}
                  className="px-5 py-2.5 rounded-lg font-semibold bg-red-600 text-white hover:bg-red-700">
                  Clear Filters
                </button>
              )}
            </div>
          ) : sortedBlocks.map(blockNum => {
            const blockUnits   = blockPlans[blockNum].filter(filterApt);
            const allBlockUnits = blockPlans[blockNum];
            if (!blockUnits.length && (searchTerm||filterStatus!=='all'||filterType!=='all')) return null;
            const isExpanded    = expandedBlocks.has(String(blockNum));
            const vacantCt      = allBlockUnits.filter(a=>!a.tenants?.length&&a.status==='vacant').length;
            const occupCt       = allBlockUnits.filter(a=>a.tenants?.length||a.status==='occupied').length;
            const overdueCt     = allBlockUnits.filter(a=>(a.tenants?.[0]?.rentStatus||a.rentStatus)==='overdue').length;

            return (
              <div key={blockNum} className={`rounded-xl border overflow-hidden ${isDarkMode?'bg-zinc-900 border-zinc-800':'bg-white border-zinc-200'} ${isExpanded?'shadow-md':'shadow-sm hover:shadow-md'} transition-shadow`}>

                {/* Block header */}
                <button onClick={()=>toggleBlock(String(blockNum))}
                  className={`w-full text-start px-5 py-4 flex items-center justify-between transition-colors ${isDarkMode?'hover:bg-zinc-800/60':'hover:bg-zinc-50'} ${isExpanded?`border-b ${isDarkMode?'border-zinc-800':'border-zinc-100'}`:''}`}>
                  <div className="flex items-center gap-4">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-bold text-sm shrink-0 ${isDarkMode?'bg-zinc-800 border border-zinc-700 text-zinc-300':'bg-zinc-100 border border-zinc-200 text-zinc-700'}`}>
                      <RiLayoutMasonryLine className={isDarkMode?'text-red-400':'text-red-500'}/>
                      ZONE {blockNum}
                    </div>
                    <span className={`text-sm ${isDarkMode?'text-zinc-500':'text-zinc-400'}`}>{allBlockUnits.length} {t('apartments', 'showrooms')}</span>
                    <div className="hidden md:flex gap-2">
                      {vacantCt>0 && <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">{vacantCt} Vacant</span>}
                      {occupCt>0 && <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{occupCt} Active</span>}
                      {overdueCt>0 && <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-500/10 text-red-400 border border-red-500/20">{overdueCt} Overdue</span>}
                    </div>
                  </div>
                  <RiArrowDownSLine size={20} className={`transition-transform duration-300 ${isExpanded?'rotate-180':''} ${isDarkMode?'text-zinc-500':'text-zinc-400'}`}/>
                </button>

                {/* Units */}
                <div className={`overflow-hidden transition-all duration-300 ${isExpanded?'max-h-[5000px] opacity-100':'max-h-0 opacity-0'}`}>
                  <div className={`p-4 ${isDarkMode?'bg-zinc-900/40':'bg-zinc-50/50'}`}>
                    {blockUnits.length===0 ? (
                      <div className={`text-center py-8 rounded-xl border-2 border-dashed ${isDarkMode?'border-zinc-700 text-zinc-500':'border-zinc-200 text-zinc-400'}`}>
                        <p className="text-sm font-medium">{t('no_matching_showrooms_in_zone', 'No matching showrooms in Zone {{blockNum}}.', { blockNum: blockNum })}</p>
                      </div>
                    ) : (() => {
                      // ── Use row_position from DB (front / back / side) ────────
                      const sorted = [...allBlockUnits].sort((a,b) => {
                        const na = parseInt(a.apartmentNumber); const nb = parseInt(b.apartmentNumber);
                        return isNaN(na)||isNaN(nb) ? a.apartmentNumber.localeCompare(b.apartmentNumber) : na - nb;
                      });
                      // Group by rowPosition from DB; fall back to lower/upper half if missing
                      const hasPosData = sorted.some(a => a.rowPosition);
                      let frontRow, backRow, cornerUnits;
                      if (hasPosData) {
                        frontRow    = sorted.filter(a => a.rowPosition === 'front');
                        backRow     = [...sorted.filter(a => a.rowPosition === 'back')].reverse(); // high→low
                        cornerUnits = sorted.filter(a => a.rowPosition === 'corner');
                      } else {
                        // fallback: split evenly
                        const half = Math.floor(sorted.length / 2);
                        frontRow    = sorted.slice(0, half);
                        backRow     = [...sorted.slice(half)].reverse();
                        cornerUnits = [];
                      }

                      // Unit card component (list-view version, richer than the tiny grid cell)
                      const UnitCard = ({ apt, small = false }) => {
                        const hasTenant  = apt.tenants?.length > 0;
                        const pT         = hasTenant ? apt.tenants[0] : null;
                        const color      = unitStatusHex(apt);
                        const rs         = pT?.rentStatus || apt.rentStatus;
                        const personName = pT?.name || apt.tenantName || apt.ownerName;
                        const balance    = parseFloat(pT?.balance || apt.balance || 0);
                        const expired    = pT && (getTenantLeaseStatus(pT)==='past' || isLeaseExpired(apt));
                        const isVisible  = filterApt(apt);
                        return (
                          <Link key={apt.id} to={`/admin/apartments/${apt.id}`}
                            className={`group relative flex flex-col rounded-xl border overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl shrink-0
                              ${small ? 'w-[110px]' : 'w-[130px]'}
                              ${!isVisible ? 'opacity-20 grayscale pointer-events-none' : ''}
                              ${isDarkMode?'bg-zinc-800 border-zinc-700 hover:border-zinc-500':'bg-white border-zinc-200 hover:border-zinc-300'}`}>
                            {/* Status bar */}
                            <div className="absolute inset-x-0 top-0 h-1" style={{backgroundColor:color}}/>
                            <div className="p-3 pt-4 flex flex-col gap-1.5">
                              {/* Number + status */}
                              <div className="flex items-start justify-between">
                                <span className={`font-mono font-black tracking-tight ${small?'text-lg':'text-xl'} ${isDarkMode?'text-white':'text-zinc-900'}`}>
                                  {apt.apartmentNumber}
                                </span>
                                <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded"
                                  style={{backgroundColor:`${color}20`, color, border:`1px solid ${color}30`}}>
                                  {expired ? '⚠' : hasTenant ? (rs||'rented') : apt.status}
                                </span>
                              </div>
                              {/* Type chip */}
                              {apt.type && (
                                <span className={`self-start text-[8px] font-bold uppercase px-1.5 py-0.5 rounded border ${typeChipClass(apt.type)}`}>
                                  {apt.type}
                                </span>
                              )}
                              {/* Person */}
                              {personName ? (
                                <p className={`text-[10px] font-semibold truncate ${isDarkMode?'text-zinc-300':'text-zinc-700'}`}>{personName}</p>
                              ) : (
                                <p className={`text-[10px] italic ${isDarkMode?'text-zinc-600':'text-zinc-400'}`}>Vacant</p>
                              )}
                              {/* Balance warning */}
                              {balance > 0 && (
                                <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-500/10 border border-red-500/20">
                                  <RiMoneyDollarCircleLine className="text-red-500 shrink-0" size={10}/>
                                  <span className="text-[9px] font-bold text-red-500 truncate">{balance}</span>
                                </div>
                              )}
                              {/* Size */}
                              {apt.size && (
                                <div className={`flex items-center gap-0.5 ${isDarkMode?'text-zinc-500':'text-zinc-400'}`}>
                                  <RiRulerLine size={10}/>
                                  <span className="text-[9px]">{apt.size} m²</span>
                                </div>
                              )}
                            </div>
                          </Link>
                        );
                      };

                      return (
                        <div className="overflow-x-auto">
                          <div className={`inline-flex gap-3 p-4 rounded-2xl border min-w-max
                            ${isDarkMode?'bg-zinc-900/60 border-zinc-700/60':'bg-zinc-50/80 border-zinc-200'}`}>

                            {/* ── Main block: back row + divider + front row ── */}
                            <div className="flex flex-col gap-2">

                              {/* Row label + Back Row (high numbers, reversed) */}
                              <div className="flex flex-col gap-1.5">
                                <div className={`flex items-center gap-2 mb-1`}>
                                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded
                                    ${isDarkMode?'bg-zinc-700 text-zinc-400':'bg-zinc-200 text-zinc-500'}`}>
                                    ↑ Back Row
                                  </span>
                                </div>
                                <div className={`flex gap-2 px-3 py-2 rounded-xl border border-dashed
                                  ${isDarkMode?'border-zinc-700 bg-zinc-800/30':'border-zinc-300 bg-white/60'}`}>
                                  {backRow.map(apt => <UnitCard key={apt.id} apt={apt}/>)}
                                </div>
                              </div>

                              {/* Divider — the physical building wall */}
                              <div className={`flex items-center gap-2 px-2`}>
                                <div className={`flex-1 h-2 rounded-full ${isDarkMode?'bg-zinc-600':'bg-zinc-300'}`}/>
                                <span className={`text-[9px] font-bold uppercase tracking-widest shrink-0 ${isDarkMode?'text-zinc-600':'text-zinc-400'}`}>
                                  ─ wall ─
                                </span>
                                <div className={`flex-1 h-2 rounded-full ${isDarkMode?'bg-zinc-600':'bg-zinc-300'}`}/>
                              </div>

                              {/* Row label + Front Row (low numbers, ascending) */}
                              <div className="flex flex-col gap-1.5">
                                <div className={`flex items-center gap-2 mb-1`}>
                                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded
                                    ${isDarkMode?'bg-zinc-700 text-zinc-400':'bg-zinc-200 text-zinc-500'}`}>
                                    ↓ Front Row
                                  </span>
                                </div>
                                <div className={`flex gap-2 px-3 py-2 rounded-xl border border-dashed
                                  ${isDarkMode?'border-zinc-700 bg-zinc-800/30':'border-zinc-300 bg-white/60'}`}>
                                  {frontRow.map(apt => <UnitCard key={apt.id} apt={apt}/>)}
                                </div>
                              </div>
                            </div>

                            {/* ── Corner column (corner units stacked vertically on the right) ── */}
                            {cornerUnits.length > 0 && (
                              <div className={`flex flex-col gap-2 justify-center ps-3 border-s
                                ${isDarkMode?'border-zinc-700':'border-zinc-300'}`}>
                                <span className={`text-[10px] font-bold uppercase tracking-widest text-center
                                  ${isDarkMode?'text-zinc-500':'text-zinc-400'}`}>Corner</span>
                                {[...cornerUnits].reverse().map(apt => (
                                  <UnitCard key={apt.id} apt={apt} small/>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BuildingView;